import { defineDropIndicator } from "./drop-indicator-dB9rZn8e.js";
import { defaultBlockAt, defineCommands, defineNodeSpec, definePlugin, findParentNode, getNodeType, insertNode, union } from "@prosekit/core";
import { TextSelection } from "@prosekit/pm/state";
import { CellSelection, TableMap, addColumnAfter, addColumnBefore, addRowAfter, addRowBefore, cellAround, cellNear, columnResizing, deleteCellSelection, deleteColumn, deleteRow, deleteTable, inSameTable, mergeCells, splitCell, tableEditing, tableNodes } from "prosemirror-tables";

//#region src/table/table-utils/convert-array-of-rows-to-table-node.ts
/**
* Convert an array of rows to a table node.
*
* @internal
*/
function convertArrayOfRowsToTableNode(tableNode, arrayOfNodes) {
	const rowsPM = [];
	const map = TableMap.get(tableNode);
	for (let rowIndex = 0; rowIndex < map.height; rowIndex++) {
		const row = tableNode.child(rowIndex);
		const rowCells = [];
		for (let colIndex = 0; colIndex < map.width; colIndex++) {
			if (!arrayOfNodes[rowIndex][colIndex]) continue;
			const cellPos = map.map[rowIndex * map.width + colIndex];
			const cell = arrayOfNodes[rowIndex][colIndex];
			const oldCell = tableNode.nodeAt(cellPos);
			const newCell = oldCell.type.createChecked(Object.assign({}, cell.attrs), cell.content, cell.marks);
			rowCells.push(newCell);
		}
		rowsPM.push(row.type.createChecked(row.attrs, rowCells, row.marks));
	}
	const newTable = tableNode.type.createChecked(tableNode.attrs, rowsPM, tableNode.marks);
	return newTable;
}

//#endregion
//#region src/table/table-utils/convert-table-node-to-array-of-rows.ts
/**
* This function will transform the table node into a matrix of rows and columns
* respecting merged cells, for example this table:
*
* ```
* ┌──────┬──────┬─────────────┐
* │  A1  │  B1  │     C1      │
* ├──────┼──────┴──────┬──────┤
* │  A2  │     B2      │      │
* ├──────┼─────────────┤  D1  │
* │  A3  │  B3  │  C3  │      │
* └──────┴──────┴──────┴──────┘
* ```
*
* will be converted to the below:
*
* ```javascript
* [
*   [A1, B1, C1, null],
*   [A2, B2, null, D1],
*   [A3, B3, C3, null],
* ]
* ```
* @internal
*/
function convertTableNodeToArrayOfRows(tableNode) {
	const map = TableMap.get(tableNode);
	const rows = [];
	const rowCount = map.height;
	const colCount = map.width;
	for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
		const row = [];
		for (let colIndex = 0; colIndex < colCount; colIndex++) {
			let cellIndex = rowIndex * colCount + colIndex;
			let cellPos = map.map[cellIndex];
			if (rowIndex > 0) {
				const topCellIndex = cellIndex - colCount;
				const topCellPos = map.map[topCellIndex];
				if (cellPos === topCellPos) {
					row.push(null);
					continue;
				}
			}
			if (colIndex > 0) {
				const leftCellIndex = cellIndex - 1;
				const leftCellPos = map.map[leftCellIndex];
				if (cellPos === leftCellPos) {
					row.push(null);
					continue;
				}
			}
			row.push(tableNode.nodeAt(cellPos));
		}
		rows.push(row);
	}
	return rows;
}

//#endregion
//#region src/table/table-utils/query.ts
/**
* Checks if the given object is a `CellSelection` instance.
*
* @public
*/
function isCellSelection(value) {
	return value instanceof CellSelection;
}
/**
* Find the closest table node.
*
* @internal
*/
function findTable($pos) {
	return findParentNode((node) => node.type.spec.tableRole === "table", $pos);
}
/**
* Try to find the anchor and head cell in the same table by using the given
* anchor and head as hit points, or fallback to the selection's anchor and
* head.
*
* @internal
*/
function findCellRange(selection, anchorHit, headHit) {
	if (anchorHit == null && headHit == null && isCellSelection(selection)) return [selection.$anchorCell, selection.$headCell];
	const anchor = anchorHit ?? headHit ?? selection.anchor;
	const head = headHit ?? anchorHit ?? selection.head;
	const doc = selection.$head.doc;
	const $anchorCell = findCellPos(doc, anchor);
	const $headCell = findCellPos(doc, head);
	if ($anchorCell && $headCell && inSameTable($anchorCell, $headCell)) return [$anchorCell, $headCell];
}
/**
* Try to find a resolved pos of a cell by using the given pos as a hit point.
*
* @internal
*/
function findCellPos(doc, pos) {
	const $pos = doc.resolve(pos);
	return cellAround($pos) || cellNear($pos);
}

//#endregion
//#region src/table/table-utils/get-cells-in-column.ts
/**
* Returns an array of cells in a column(s), where `columnIndex` could be a column index or an array of column indexes.
*
* @internal
*/
function getCellsInColumn(columnIndexes, selection) {
	const table = findTable(selection.$from);
	if (!table) return;
	const map = TableMap.get(table.node);
	const indexes = Array.isArray(columnIndexes) ? columnIndexes : [columnIndexes];
	return indexes.filter((index) => index >= 0 && index <= map.width - 1).flatMap((index) => {
		const cells = map.cellsInRect({
			left: index,
			right: index + 1,
			top: 0,
			bottom: map.height
		});
		return cells.map((nodePos) => {
			const node = table.node.nodeAt(nodePos);
			const pos = nodePos + table.start;
			return {
				pos,
				start: pos + 1,
				node,
				depth: table.depth + 2
			};
		});
	});
}

//#endregion
//#region src/table/table-utils/get-cells-in-row.ts
/**
* Returns an array of cells in a row(s), where `rowIndex` could be a row index or an array of row indexes.
*
* @internal
*/
function getCellsInRow(rowIndex, selection) {
	const table = findTable(selection.$from);
	if (!table) return;
	const map = TableMap.get(table.node);
	const indexes = Array.isArray(rowIndex) ? rowIndex : [rowIndex];
	return indexes.filter((index) => index >= 0 && index <= map.height - 1).flatMap((index) => {
		const cells = map.cellsInRect({
			left: 0,
			right: map.width,
			top: index,
			bottom: index + 1
		});
		return cells.map((nodePos) => {
			const node = table.node.nodeAt(nodePos);
			const pos = nodePos + table.start;
			return {
				pos,
				start: pos + 1,
				node,
				depth: table.depth + 2
			};
		});
	});
}

//#endregion
//#region src/table/table-utils/get-selection-range-in-column.ts
/**
* Returns a range of rectangular selection spanning all merged cells around a
* column at index `columnIndex`.
*
* Original implementation from Atlassian (Apache License 2.0)
*
* https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/5f91cb871e8248bc3bae5ddc30bb9fd9200fadbb/editor/editor-tables/src/utils/get-selection-range-in-column.ts#editor/editor-tables/src/utils/get-selection-range-in-column.ts
*
* @internal
*/
function getSelectionRangeInColumn(tr, startColIndex, endColIndex = startColIndex) {
	let startIndex = startColIndex;
	let endIndex = endColIndex;
	for (let i = startColIndex; i >= 0; i--) {
		const cells = getCellsInColumn(i, tr.selection);
		if (cells) cells.forEach((cell) => {
			const maybeEndIndex = cell.node.attrs.colspan + i - 1;
			if (maybeEndIndex >= startIndex) startIndex = i;
			if (maybeEndIndex > endIndex) endIndex = maybeEndIndex;
		});
	}
	for (let i = startColIndex; i <= endIndex; i++) {
		const cells = getCellsInColumn(i, tr.selection);
		if (cells) cells.forEach((cell) => {
			const maybeEndIndex = cell.node.attrs.colspan + i - 1;
			if (cell.node.attrs.colspan > 1 && maybeEndIndex > endIndex) endIndex = maybeEndIndex;
		});
	}
	const indexes = [];
	for (let i = startIndex; i <= endIndex; i++) {
		const maybeCells = getCellsInColumn(i, tr.selection);
		if (maybeCells && maybeCells.length > 0) indexes.push(i);
	}
	startIndex = indexes[0];
	endIndex = indexes[indexes.length - 1];
	const firstSelectedColumnCells = getCellsInColumn(startIndex, tr.selection);
	const firstRowCells = getCellsInRow(0, tr.selection);
	if (!firstSelectedColumnCells || !firstRowCells) return;
	const $anchor = tr.doc.resolve(firstSelectedColumnCells[firstSelectedColumnCells.length - 1].pos);
	let headCell;
	for (let i = endIndex; i >= startIndex; i--) {
		const columnCells = getCellsInColumn(i, tr.selection);
		if (columnCells && columnCells.length > 0) {
			for (let j = firstRowCells.length - 1; j >= 0; j--) if (firstRowCells[j].pos === columnCells[0].pos) {
				headCell = columnCells[0];
				break;
			}
			if (headCell) break;
		}
	}
	if (!headCell) return;
	const $head = tr.doc.resolve(headCell.pos);
	return {
		$anchor,
		$head,
		indexes
	};
}

//#endregion
//#region src/table/table-utils/move-row-in-array-of-rows.ts
/**
* Move a row in an array of rows.
*
* @internal
*/
function moveRowInArrayOfRows(rows, indexesOrigin, indexesTarget, directionOverride) {
	const direction = indexesOrigin[0] > indexesTarget[0] ? -1 : 1;
	const rowsExtracted = rows.splice(indexesOrigin[0], indexesOrigin.length);
	const positionOffset = rowsExtracted.length % 2 === 0 ? 1 : 0;
	let target;
	if (directionOverride === -1 && direction === 1) target = indexesTarget[0] - 1;
	else if (directionOverride === 1 && direction === -1) target = indexesTarget[indexesTarget.length - 1] - positionOffset + 1;
	else target = direction === -1 ? indexesTarget[0] : indexesTarget[indexesTarget.length - 1] - positionOffset;
	rows.splice(target, 0, ...rowsExtracted);
	return rows;
}

//#endregion
//#region src/table/table-utils/transpose.ts
/**
* Transposes a 2D array by flipping columns to rows.
*
* Transposition is a familiar algebra concept where the matrix is flipped
* along its diagonal. For more details, see:
* https://en.wikipedia.org/wiki/Transpose
*
* @example
* ```javascript
* const arr = [
*   ['a1', 'a2', 'a3'],
*   ['b1', 'b2', 'b3'],
*   ['c1', 'c2', 'c3'],
*   ['d1', 'd2', 'd3'],
* ];
*
* const result = transpose(arr);
* result === [
*   ['a1', 'b1', 'c1', 'd1'],
*   ['a2', 'b2', 'c2', 'd2'],
*   ['a3', 'b3', 'c3', 'd3'],
* ]
* ```
*/
function transpose(array) {
	return array[0].map((_, i) => {
		return array.map((column) => column[i]);
	});
}

//#endregion
//#region src/table/table-utils/move-column.ts
/**
* Move a column from index `origin` to index `target`.
*
* @internal
*/
function moveColumn(moveColParams) {
	const { tr, originIndex, targetIndex, select, pos } = moveColParams;
	const $pos = tr.doc.resolve(pos);
	const table = findTable($pos);
	if (!table) return false;
	const indexesOriginColumn = getSelectionRangeInColumn(tr, originIndex)?.indexes;
	const indexesTargetColumn = getSelectionRangeInColumn(tr, targetIndex)?.indexes;
	if (!indexesOriginColumn || !indexesTargetColumn) return false;
	if (indexesOriginColumn.includes(targetIndex)) return false;
	const newTable = moveTableColumn$1(table.node, indexesOriginColumn, indexesTargetColumn, 0);
	tr.replaceWith(table.pos, table.pos + table.node.nodeSize, newTable);
	if (!select) return true;
	const map = TableMap.get(newTable);
	const start = table.start;
	const index = targetIndex;
	const lastCell = map.positionAt(map.height - 1, index, newTable);
	const $lastCell = tr.doc.resolve(start + lastCell);
	const firstCell = map.positionAt(0, index, newTable);
	const $firstCell = tr.doc.resolve(start + firstCell);
	tr.setSelection(CellSelection.colSelection($lastCell, $firstCell));
	return true;
}
function moveTableColumn$1(table, indexesOrigin, indexesTarget, direction) {
	let rows = transpose(convertTableNodeToArrayOfRows(table));
	rows = moveRowInArrayOfRows(rows, indexesOrigin, indexesTarget, direction);
	rows = transpose(rows);
	return convertArrayOfRowsToTableNode(table, rows);
}

//#endregion
//#region src/table/table-utils/get-selection-range-in-row.ts
/**
* Returns a range of rectangular selection spanning all merged cells around a
* row at index `rowIndex`.
*
* Original implementation from Atlassian (Apache License 2.0)
*
* https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/5f91cb871e8248bc3bae5ddc30bb9fd9200fadbb/editor/editor-tables/src/utils/get-selection-range-in-row.ts#editor/editor-tables/src/utils/get-selection-range-in-row.ts
*
* @internal
*/
function getSelectionRangeInRow(tr, startRowIndex, endRowIndex = startRowIndex) {
	let startIndex = startRowIndex;
	let endIndex = endRowIndex;
	for (let i = startRowIndex; i >= 0; i--) {
		const cells = getCellsInRow(i, tr.selection);
		if (cells) cells.forEach((cell) => {
			const maybeEndIndex = cell.node.attrs.rowspan + i - 1;
			if (maybeEndIndex >= startIndex) startIndex = i;
			if (maybeEndIndex > endIndex) endIndex = maybeEndIndex;
		});
	}
	for (let i = startRowIndex; i <= endIndex; i++) {
		const cells = getCellsInRow(i, tr.selection);
		if (cells) cells.forEach((cell) => {
			const maybeEndIndex = cell.node.attrs.rowspan + i - 1;
			if (cell.node.attrs.rowspan > 1 && maybeEndIndex > endIndex) endIndex = maybeEndIndex;
		});
	}
	const indexes = [];
	for (let i = startIndex; i <= endIndex; i++) {
		const maybeCells = getCellsInRow(i, tr.selection);
		if (maybeCells && maybeCells.length > 0) indexes.push(i);
	}
	startIndex = indexes[0];
	endIndex = indexes[indexes.length - 1];
	const firstSelectedRowCells = getCellsInRow(startIndex, tr.selection);
	const firstColumnCells = getCellsInColumn(0, tr.selection);
	if (!firstSelectedRowCells || !firstColumnCells) return;
	const $anchor = tr.doc.resolve(firstSelectedRowCells[firstSelectedRowCells.length - 1].pos);
	let headCell;
	for (let i = endIndex; i >= startIndex; i--) {
		const rowCells = getCellsInRow(i, tr.selection);
		if (rowCells && rowCells.length > 0) {
			for (let j = firstColumnCells.length - 1; j >= 0; j--) if (firstColumnCells[j].pos === rowCells[0].pos) {
				headCell = rowCells[0];
				break;
			}
			if (headCell) break;
		}
	}
	if (!headCell) return;
	const $head = tr.doc.resolve(headCell.pos);
	return {
		$anchor,
		$head,
		indexes
	};
}

//#endregion
//#region src/table/table-utils/move-row.ts
/**
* Move a row from index `origin` to index `target`.
*
* @internal
*/
function moveRow(moveRowParams) {
	const { tr, originIndex, targetIndex, select, pos } = moveRowParams;
	const $pos = tr.doc.resolve(pos);
	const table = findTable($pos);
	if (!table) return false;
	const indexesOriginRow = getSelectionRangeInRow(tr, originIndex)?.indexes;
	const indexesTargetRow = getSelectionRangeInRow(tr, targetIndex)?.indexes;
	if (!indexesOriginRow || !indexesTargetRow) return false;
	if (indexesOriginRow.includes(targetIndex)) return false;
	const newTable = moveTableRow$1(table.node, indexesOriginRow, indexesTargetRow, 0);
	tr.replaceWith(table.pos, table.pos + table.node.nodeSize, newTable);
	if (!select) return true;
	const map = TableMap.get(newTable);
	const start = table.start;
	const index = targetIndex;
	const lastCell = map.positionAt(index, map.width - 1, newTable);
	const $lastCell = tr.doc.resolve(start + lastCell);
	const firstCell = map.positionAt(index, 0, newTable);
	const $firstCell = tr.doc.resolve(start + firstCell);
	tr.setSelection(CellSelection.rowSelection($lastCell, $firstCell));
	return true;
}
function moveTableRow$1(table, indexesOrigin, indexesTarget, direction) {
	let rows = convertTableNodeToArrayOfRows(table);
	rows = moveRowInArrayOfRows(rows, indexesOrigin, indexesTarget, direction);
	return convertArrayOfRowsToTableNode(table, rows);
}

//#endregion
//#region src/table/table-commands/move-table-column.ts
/**
* Move a table column from index `origin` to index `target`.
*
* @public
*/
function moveTableColumn(options) {
	return (state, dispatch) => {
		const { from: originIndex, to: targetIndex, select = true, pos = state.selection.from } = options;
		const tr = state.tr;
		if (moveColumn({
			tr,
			originIndex,
			targetIndex,
			select,
			pos
		})) {
			dispatch?.(tr);
			return true;
		}
		return false;
	};
}

//#endregion
//#region src/table/table-commands/move-table-row.ts
/**
* Move a table row from index `origin` to index `target`.
*
* @public
*/
function moveTableRow(options) {
	return (state, dispatch) => {
		const { from: originIndex, to: targetIndex, select = true, pos = state.selection.from } = options;
		const tr = state.tr;
		if (moveRow({
			tr,
			originIndex,
			targetIndex,
			select,
			pos
		})) {
			dispatch?.(tr);
			return true;
		}
		return false;
	};
}

//#endregion
//#region src/table/table-commands.ts
function createEmptyTable(schema, row, col, header) {
	const tableType = getNodeType(schema, "table");
	const tableRowType = getNodeType(schema, "tableRow");
	const tableCellType = getNodeType(schema, "tableCell");
	const tableHeaderCellType = getNodeType(schema, "tableHeaderCell");
	if (header) {
		const headerCell = tableHeaderCellType.createAndFill();
		const headerCells = repeat(headerCell, col);
		const headerRow = tableRowType.createAndFill(null, headerCells);
		const bodyCell = tableCellType.createAndFill();
		const bodyCells = repeat(bodyCell, col);
		const bodyRow = tableRowType.createAndFill(null, bodyCells);
		const bodyRows = repeat(bodyRow, row - 1);
		return tableType.createAndFill(null, [headerRow, ...bodyRows]);
	} else {
		const bodyCell = tableCellType.createAndFill();
		const bodyCells = repeat(bodyCell, col);
		const bodyRow = tableRowType.createAndFill(null, bodyCells);
		const bodyRows = repeat(bodyRow, row);
		return tableType.createAndFill(null, bodyRows);
	}
}
function repeat(node, length) {
	return Array(length).fill(node);
}
/**
* Insert a table node with the given number of rows and columns, and optionally
* a header row.
*
* @param options
*
* @public
*/
function insertTable(options) {
	return (state, dispatch, view) => {
		const { row, col, header = false } = options;
		const table = createEmptyTable(state.schema, row, col, header);
		return insertNode({ node: table })(state, dispatch, view);
	};
}
/**
* When the selection is in a table node, create a default block after the table
* table, and move the cursor there.
*
* @public
*/
const exitTable = (state, dispatch) => {
	const { $head, $anchor } = state.selection;
	if (!$head.sameParent($anchor)) return false;
	let tableStart = -1;
	let tableDepth = -1;
	for (let depth = $head.depth; depth >= 0; depth--) {
		const node$1 = $head.node(depth);
		if (node$1.type.spec.tableRole === "table") {
			tableStart = $head.before(depth);
			tableDepth = depth;
		}
	}
	if (tableStart < 0 || tableDepth <= 0) return false;
	const above = $head.node(tableDepth - 1);
	const after = $head.indexAfter(tableDepth - 1);
	const type = defaultBlockAt(above.contentMatchAt(after));
	const node = type?.createAndFill();
	if (!type || !node || !above.canReplaceWith(after, after, type)) return false;
	if (dispatch) {
		const pos = $head.after(tableDepth);
		const tr = state.tr.replaceWith(pos, pos, node);
		tr.setSelection(TextSelection.near(tr.doc.resolve(pos), 1));
		dispatch(tr.scrollIntoView());
	}
	return true;
};
/**
* @public
*/
function selectTableColumn(options) {
	return (state, dispatch) => {
		const range = findCellRange(state.selection, options?.anchor, options?.head);
		if (!range) return false;
		if (dispatch) {
			const [$anchorCell, $headCell] = range;
			const selection = CellSelection.colSelection($anchorCell, $headCell);
			dispatch(state.tr.setSelection(selection));
		}
		return true;
	};
}
/**
* @public
*/
function selectTableRow(options) {
	return (state, dispatch) => {
		const range = findCellRange(state.selection, options?.anchor, options?.head);
		if (!range) return false;
		if (dispatch) {
			const [$anchorCell, $headCell] = range;
			const selection = CellSelection.rowSelection($anchorCell, $headCell);
			dispatch(state.tr.setSelection(selection));
		}
		return true;
	};
}
/**
* @public
*/
function selectTableCell(options) {
	return (state, dispatch) => {
		const $cellPos = findCellPos(state.doc, options?.pos ?? state.selection.anchor);
		if (!$cellPos) return false;
		if (dispatch) {
			const selection = new CellSelection($cellPos);
			dispatch(state.tr.setSelection(selection));
		}
		return true;
	};
}
/**
* @public
*/
function selectTable(options) {
	return (state, dispatch) => {
		const $pos = options?.pos ? state.doc.resolve(options.pos) : state.selection.$anchor;
		const table = findTable($pos);
		if (!table) return false;
		const map = TableMap.get(table.node);
		if (map.map.length === 0) return false;
		if (dispatch) {
			let tr = state.tr;
			const firstCellPosInTable = map.map[0];
			const lastCellPosInTable = map.map[map.map.length - 1];
			const firstCellPos = table.pos + firstCellPosInTable + 1;
			const lastCellPos = table.pos + lastCellPosInTable + 1;
			const $firstCellPos = tr.doc.resolve(firstCellPos);
			const $lastCellPos = tr.doc.resolve(lastCellPos);
			const selection = new CellSelection($firstCellPos, $lastCellPos);
			tr = tr.setSelection(selection);
			dispatch?.(tr);
		}
		return true;
	};
}
/**
* Adds commands for working with `table` nodes.
*
* @public
*/
function defineTableCommands() {
	return defineCommands({
		insertTable,
		exitTable: () => exitTable,
		selectTable,
		selectTableCell,
		selectTableColumn,
		selectTableRow,
		addTableColumnBefore: () => addColumnBefore,
		addTableColumnAfter: () => addColumnAfter,
		addTableRowAbove: () => addRowBefore,
		addTableRowBelow: () => addRowAfter,
		deleteTable: () => deleteTable,
		deleteTableColumn: () => deleteColumn,
		deleteTableRow: () => deleteRow,
		deleteCellSelection: () => deleteCellSelection,
		mergeTableCells: () => mergeCells,
		splitTableCell: () => splitCell,
		moveTableRow,
		moveTableColumn
	});
}

//#endregion
//#region src/table/table-drop-indicator.ts
/**
* Hides the drop indicator when dragging a table column or row by using the
* table handle.
*
* @internal
*/
function defineTableDropIndicator() {
	return defineDropIndicator({ onDrag });
}
const matchMap = /* @__PURE__ */ new WeakMap();
const onDrag = ({ event }) => {
	const dataTransfer = event.dataTransfer;
	if (!dataTransfer) return true;
	let match;
	if (matchMap.has(dataTransfer)) match = matchMap.get(dataTransfer);
	else {
		const types = dataTransfer.types;
		match = types.includes("application/x-prosekit-table-handle-drag");
		matchMap.set(dataTransfer, match);
	}
	return !match;
};

//#endregion
//#region src/table/table-plugins.ts
/**
* @public
*/
function defineTablePlugins() {
	return definePlugin([tableEditing(), columnResizing()]);
}

//#endregion
//#region src/table/table-spec.ts
const cellContent = "block+";
const cellAttrs = {
	colspan: { default: 1 },
	rowspan: { default: 1 },
	colwidth: { default: null }
};
const specs = tableNodes({
	tableGroup: "block",
	cellContent,
	cellAttributes: {}
});
/**
* @internal
*/
function defineTableSpec() {
	return defineNodeSpec({
		...specs["table"],
		content: "tableRow+",
		name: "table"
	});
}
/**
* @internal
*/
function defineTableRowSpec() {
	return defineNodeSpec({
		...specs["table_row"],
		content: "(tableCell | tableHeaderCell)*",
		name: "tableRow"
	});
}
/**
* @internal
*/
function defineTableCellSpec() {
	return defineNodeSpec({
		...specs["table_cell"],
		name: "tableCell",
		attrs: cellAttrs
	});
}
function defineTableHeaderCellSpec() {
	return defineNodeSpec({
		...specs["table_header"],
		name: "tableHeaderCell",
		attrs: cellAttrs
	});
}

//#endregion
//#region src/table/table.ts
/**
* @public
*/
function defineTable() {
	return union(defineTableSpec(), defineTableRowSpec(), defineTableCellSpec(), defineTableHeaderCellSpec(), defineTablePlugins(), defineTableCommands(), defineTableDropIndicator());
}

//#endregion
export { defineTable, defineTableCellSpec, defineTableCommands, defineTableDropIndicator, defineTableHeaderCellSpec, defineTablePlugins, defineTableRowSpec, defineTableSpec, exitTable, findTable, insertTable, isCellSelection, moveTableColumn, moveTableRow, selectTable, selectTableCell, selectTableColumn, selectTableRow };